// pages/DeviceGroups/ViewDeviceGroup.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Layout,
  Card,
  Descriptions,
  Space,
  Typography,
  Button,
  Divider,
  Skeleton,
  message,
  Table,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, TeamOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { getDeviceGroupById } from '@/api/devicegroupService';
import { listAllDevices } from '@/api/deviceService';
import type { DeviceEntity } from '@/interface/responses/deviceResponse';
import type { DeviceListQueryWithFilters } from '@/api/deviceService';

const { Content } = Layout;
const { Title, Text } = Typography;

type DeviceBrief = {
  id: number;
  device_type: string;
  device_serial: string;
  firmware_version?: string;
  license_id?: number;
  user_id?: number;
  last_heartbeat?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
};

type GroupDetail = {
  id: number;
  name: string;
  devices?: DeviceBrief[]; // clarified type
  deviceCount?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
};

const ViewDeviceGroup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const numericId = useMemo(() => (id ? Number(id) : undefined), [id]);

  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState<GroupDetail | undefined>(undefined);

  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devices, setDevices] = useState<DeviceEntity[]>([]);
  const [devicesTotal, setDevicesTotal] = useState(0);
  const [devicesPage, setDevicesPage] = useState(1);
  const [devicesPageSize, setDevicesPageSize] = useState(10);

  const loadDevices = async () => {
    if (!numericId) return;

    setDevicesLoading(true);
    try {
      const query: DeviceListQueryWithFilters = {
        page: devicesPage,
        limit: devicesPageSize,
        deviceGroupIds: [numericId], // ✅ Filter by current group only
      };

      const response = await listAllDevices(query);
      setDevices(response.data || []);
      setDevicesTotal(response.pagination?.totalRecords || 0);
    } catch (err: any) {
      console.error('Failed to fetch devices:', err);
      message.error('Failed to fetch devices');
      setDevices([]);
      setDevicesTotal(0);
    } finally {
      setDevicesLoading(false);
    }
  };

  const load = async () => {
    if (!numericId) return;
    setLoading(true);
    try {
      const detail = await getDeviceGroupById(numericId);
      setGroup(detail as unknown as GroupDetail);
    } catch (err: any) {
      message.error(err?.message || 'Failed to fetch device group');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (numericId) load();
  }, [numericId]);
  
  useEffect(() => {
    if (numericId) loadDevices();
  }, [numericId, devicesPage, devicesPageSize]); // ✅ REMOVED: filter dependencies

  const handleChangePage = (page: number, pageSize: number) => {
    setDevicesPage(page);
    setDevicesPageSize(pageSize);
  };

  const handleBack = () => {
    navigate('/device-management', {
      replace: false,
      state: { activeTab: 'device-groups' },
    });
  };

  const headerActions = (
    <Space wrap>
      <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
        Back
      </Button>
      <Button icon={<ReloadOutlined />} onClick={() => { load(); loadDevices(); }} loading={loading || devicesLoading}>
        Refresh
      </Button>
    </Space>
  );

  const fmt = (d?: string) => (d ? dayjs(d).format('YYYY-MM-DD HH:mm:ss') : '—');

  // ✅ SIMPLIFIED: Basic columns without filters and S.No
  const columns = [
    {
      title: 'Device Name',
      dataIndex: 'device_serial',
      key: 'device_serial',
      render: (v: string) => <span className="font-medium">{v}</span>,
    },
    {
      title: 'Device Type',
      dataIndex: 'device_type',
      key: 'device_type',
      width: 100,
      render: (v?: string) => v || '—',
    },
    {
      title: 'Firmware Version',
      dataIndex: 'firmware_version',
      key: 'firmware_version',
      width: 120,
      render: (v?: string) => v || '—',
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      ellipsis: true,
      render: (v?: string) => v || '—',
    },
    {
      title: 'Email',
      key: 'email',
      dataIndex: ['user', 'email'],
      ellipsis: true,
      render: (_: any, rec: DeviceEntity) => rec.user?.email || '—',
    },
    {
      title: 'Phone Number',
      key: 'phone_number',
      dataIndex: ['user', 'phone_number'],
      width: 150,
      render: (_: any, rec: DeviceEntity) => rec.user?.phone_number || '—',
    },
    {
      title: 'Last Heartbeat',
      dataIndex: 'last_heartbeat',
      key: 'last_heartbeat',
      width: 210,
      render: (v?: string) => (v ? `${fmt(v)} (${dayjs(v).fromNow?.() || ''})` : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, rec: DeviceEntity) => (
        <Button
          type="text"
          size="small"
          icon={<EyeOutlined />}
          title="View Device"
          onClick={() =>
            navigate(`/device-management/view/${rec.id}`, {
              state: { activeTab: 'device-groups' },
            })
          }
        />
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ padding: 24 }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space align="center" size="large">
              <TeamOutlined />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {group?.name || 'Device Group'}
                </Title>
              </div>
            </Space>
            {headerActions}
          </Space>

          {/* Group Details */}
          <Card>
            {loading && !group ? (
              <Skeleton active />
            ) : !group ? (
              <Text type="secondary">Device group not found.</Text>
            ) : (
              <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <Divider>Group Details</Divider>
                <Descriptions column={2} size="middle" bordered>
                  <Descriptions.Item label="Group Name">{group.name}</Descriptions.Item>
                  <Descriptions.Item label="Number of associated devices">
                    {devicesTotal} devices
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{fmt(group.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="Updated At">{fmt(group.updatedAt)}</Descriptions.Item>
                </Descriptions>
              </Space>
            )}
          </Card>

          {/* ✅ SIMPLIFIED: Basic table without filters */}
          <Card title="Associated Devices" bodyStyle={{ paddingTop: 0 }}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={devices}
              pagination={{
                current: devicesPage,
                pageSize: devicesPageSize,
                total: devicesTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                onChange: handleChangePage,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} devices`,
              }}
              scroll={{ x: 1000 }} // ✅ Reduced width since no S.No column
              loading={devicesLoading}
            />
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default ViewDeviceGroup;
